"use client";
import { useCallback } from "react"

// --Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --Lib ---
import { isExtensionAvailable } from "@/tiptap/lib/tiptap-utils"
import {
  getTable,
  RESIZE_MIN_WIDTH,
} from "@/tiptap/nodes/table-node/lib/tiptap-table-utils"

// --Icons ---
import { MoveHorizontalIcon } from "@/tiptap/icons/move-horizontal-icon"

/**
 * Required Tiptap extensions for this feature to work.
 * - `table` to target the node and update attributes
 * - `tableHandleExtension` (or your table controls) to ensure table tooling is enabled
 */
const REQUIRED_EXTENSIONS = ["table", "tableHandleExtension"]
const FIT_TO_WIDTH_INSET = 8

function isElement(value) {
  const elementNodeType = typeof Node === "undefined" ? 1 : Node.ELEMENT_NODE
  return value?.nodeType === elementNodeType
}

function getElementContentWidth(element) {
  if (!isElement(element)) return 0

  const style = getComputedStyle(element)
  const paddingLeft =
    parseFloat(style.paddingLeft || element.style?.paddingLeft) || 0
  const paddingRight =
    parseFloat(style.paddingRight || element.style?.paddingRight) || 0
  const measuredWidth =
    element.clientWidth || element.getBoundingClientRect?.().width || 0

  return Math.max(0, Math.floor(measuredWidth - paddingLeft - paddingRight))
}

export function getTableFitContainerWidth(editor, tablePos) {
  const nodeDOM = editor?.view?.nodeDOM?.(tablePos)
  const tableWrapper = isElement(nodeDOM)
    ? (
        nodeDOM.classList.contains("tableWrapper")
          ? nodeDOM
          : nodeDOM.closest?.(".tableWrapper")
      )
    : null
  // Prefer the direct scroll wrapper so a nested table's scroll area isn't measured.
  const scrollWrapper =
    tableWrapper?.querySelector(":scope > .tableWrapper-scroll") ||
    tableWrapper?.querySelector(".tableWrapper-scroll")
  const scrollWrapperWidth = getElementContentWidth(scrollWrapper)

  if (scrollWrapperWidth > 0) return scrollWrapperWidth

  return getElementContentWidth(editor?.view?.dom)
}

export function calculateTableFitColumnWidths(containerWidth, columnCount) {
  if (
    !Number.isFinite(containerWidth) ||
    containerWidth <= 0 ||
    columnCount <= 0
  ) {
    return []
  }

  const targetWidth = Math.floor(containerWidth) - FIT_TO_WIDTH_INSET
  if (targetWidth <= 0) return []

  const baseWidth = Math.floor(targetWidth / columnCount)
  const remainder = targetWidth - baseWidth * columnCount

  return Array.from(
    { length: columnCount },
    (_value, index) => baseWidth + (index < remainder ? 1 : 0),
  )
}

/**
 * Returns whether a "fit to width" action can run in the current state.
 * Checks: editor presence, editability, required extensions,
 * and that the selection is somewhere inside a table.
 */
function canFitTableToWidth(editor) {
  if (
    !editor ||
    !editor.isEditable ||
    !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)
  ) {
    return false
  }

  try {
    return (editor.isActive("table") ||
    editor.isActive("tableCell") || editor.isActive("tableHeader"));
  } catch {
    return false
  }
}

/**
 * Applies fit-to-width column sizes to cells in `table` only.
 * Uses the table map so nested tables inside cells are never visited.
 */
export function applyFitWidthsToTableCells(tr, table, columnWidths) {
  const seenCellPositions = new Set()

  table.map.map.forEach((relativeCellPos) => {
    if (seenCellPositions.has(relativeCellPos)) return
    seenCellPositions.add(relativeCellPos)

    const cell = table.node.nodeAt(relativeCellPos)
    if (
      !cell ||
      (cell.type.name !== "tableCell" && cell.type.name !== "tableHeader")
    ) {
      return
    }

    const absolutePos = table.start + relativeCellPos
    const colspan = cell.attrs.colspan || 1
    const startColumn = table.map.colCount(relativeCellPos)
    const colwidthArray = Array.from(
      { length: colspan },
      (_value, index) => columnWidths[startColumn + index] ?? RESIZE_MIN_WIDTH,
    )

    tr.setNodeMarkup(absolutePos, undefined, {
      ...cell.attrs,
      colwidth: colwidthArray,
    })
  })

  return tr
}

/**
 * Automatically adjusts table column widths to fit the table wrapper's width.
 *
 * This function finds the table containing the current selection and distributes
 * the visible scroll-wrapper width across all columns, avoiding an overflow
 * scrollbar when the fitted table can fit inside the editor.
 *
 * Only cells belonging to that table are updated — nested tables are ignored so
 * their cells can't throw TableMap lookups and abort the whole resize.
 *
 * @param editor - The ProseMirror editor instance, or null
 * @returns true if the table width was successfully set, false otherwise
 */
export function setTableAutoWidth(editor) {
  if (!canFitTableToWidth(editor) || !editor) return false

  try {
    const table = getTable(editor)
    if (!table) return false

    const columnCount = table.map.width
    if (columnCount === 0) return false

    const columnWidths = calculateTableFitColumnWidths(
      getTableFitContainerWidth(editor, table.pos),
      columnCount,
    )

    if (columnWidths.length === 0) return false

    const tr = applyFitWidthsToTableCells(editor.state.tr, table, columnWidths)

    if (tr.docChanged) {
      editor.view.dispatch(tr)
    }

    return true
  } catch (error) {
    console.error("Error setting table auto width:", error)
    return false
  }
}

/**
 * Executes the "fit to width" operation. Safely no-ops if unavailable.
 * Returns `true` on success, `false` otherwise.
 */
function tableFitToWidth({
  editor
}) {
  if (!canFitTableToWidth(editor) || !editor) {
    return false
  }

  try {
    return setTableAutoWidth(editor);
  } catch (error) {
    console.error("Error adjusting table width:", error)
    return false
  }
}

/**
 * Determines whether a UI control should be visible based on the editor
 * state and `hideWhenUnavailable` setting.
 */
function shouldShowButton(
  {
    editor,
    hideWhenUnavailable
  }
) {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false

  return hideWhenUnavailable ? canFitTableToWidth(editor) : true;
}

/**
 * React hook that provides a **Fit table to container width** action for Tiptap.
 *
 * What it does:
 * - Measures the rendered table wrapper
 * - Sets cell-level `colwidth` values that sum to the available width
 *
 * Returned API:
 * - `isVisible`: whether a button should be shown
 * - `canFitToWidth`: whether the action can execute now
 * - `handleFitToWidth()`: runs the action; returns `true` on success
 * - `label`: UI label, e.g. "Fit to width"
 * - `Icon`: a presentational icon component
 *
 * @example
 * // Minimal button
 * function FitToWidthButton() {
 *   const { isVisible, canFitToWidth, handleFitToWidth, label, Icon } =
 *     useTableFitToWidth({ hideWhenUnavailable: true })
 *
 *   if (!isVisible) return null
 *   return (
 *     <button onClick={handleFitToWidth} disabled={!canFitToWidth} aria-label={label}>
 *       <Icon /> {label}
 *     </button>
 *   )
 * }
 *
 * @example
 * // Using a provided editor instance and a success callback
 * function FitToWidthWithCallback({ editor }: { editor: Editor }) {
 *   const { handleFitToWidth, canFitToWidth } = useTableFitToWidth({
 *     editor,
 *     hideWhenUnavailable: true,
 *     onWidthAdjusted: () => console.log("Table set to auto width"),
 *   })
 *   return (
 *     <button onClick={handleFitToWidth} disabled={!canFitToWidth}>
 *       Fit to container
 *     </button>
 *   )
 * }
 */
export function useTableFitToWidth(config = {}) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onWidthAdjusted,
  } = config

  const { editor } = useTiptapEditor(providedEditor)

  const isVisible = shouldShowButton({
    editor,
    hideWhenUnavailable,
  })

  const canPerformAction = canFitTableToWidth(editor)

  const handleFitToWidth = useCallback(() => {
    const success = tableFitToWidth({ editor })
    if (success) onWidthAdjusted?.()
    return success
  }, [editor, onWidthAdjusted])

  const label = "Fit to width"
  const Icon = MoveHorizontalIcon

  return {
    isVisible,
    canFitToWidth: canPerformAction,
    handleFitToWidth,
    label,
    Icon,
  }
}
